import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import type { RepairOrder } from 'modules/selfServices/types/SelfService';

import usePreAuthorizedAmounts from 'hooks/usePreAuthorizedAmounts';

import Icon from 'components/ui/Icon';
import { DEFAULT_ROW_SIZE } from 'components/ui/ExpandableList';
import RepairOrderItem from 'components/RepairOrder/RepairOrderItem';

import PreAuthorizedAmountLine from './PreAuthorizedAmountLine';

const RepairOrderExpandableList: React.FC<{ data: RepairOrder[] }> = ({ data }) => {

  const { customerPreAuthorizedAmount, hasCustomerPreAuthorizedAmount } = usePreAuthorizedAmounts();

  const showCollapseButton = data?.length > DEFAULT_ROW_SIZE;
  const [collapsed, setCollapsed] = React.useState(showCollapseButton);

  const grouped = React.useMemo(
    () => data?.reduce<Record<string, RepairOrder[]>>((acc, work, index) => {
      const canPush = !collapsed || index < DEFAULT_ROW_SIZE;
      if (canPush) {
        const { workOrderTag = '' } = work;
        return {
          ...acc,
          [workOrderTag]: [...(acc[workOrderTag] ?? []), work],
        };
      }

      return acc;
    }, {}),
    [data, collapsed],
  );

  const handleClick = React.useCallback(() => setCollapsed((prev) => !prev), []);

  return (
    <>
      <div className="flex flex-col gap-2 items-center w-full max-w-content">
        <FormattedMessage
          tagName="h2"
          id="preapprovedWorks"
          defaultMessage="Pre-approved works"
        />
        <p className="text-center text-low">
          <FormattedMessage
            id="repairOrder.indicationWithRepairOrder"
            defaultMessage="Please find the subject of your appointment below."
          />
        </p>
        <div className="flex flex-col gap-6 bg-card-bg rounded-card w-full p-2xs">
          {Object.entries(grouped).map(([workOrderTag, items]) => (
            <div key={workOrderTag} className="flex flex-col gap-xs px-2xs">
              {workOrderTag && (
                <div className="bg-white font-bold kiosk:text-3xl p-2 rounded-md">
                  {workOrderTag}
                </div>
              )}
              {items.map((item, index) => (
                <RepairOrderItem key={index} {...item} className="border-border-default border-b pb-xs last:border-b-0 first:pt-xs" />
              ))}
            </div>
          ))}

          {showCollapseButton && (
            <div
              onClick={handleClick}
              data-testid="ExpandCollapseBtn"
              className="cursor-pointer -mt-l border-border-default border-t mx-2xs pt-xs pb-2xs"
            >
              <div className="flex justify-center items-center kiosk:text-2xl kioskSharebox:text-xl">
                {collapsed && <FormattedMessage id="list.viewAll" defaultMessage="View all" />}
                {!collapsed && <FormattedMessage id="list.reduce" defaultMessage="Reduce" />}
                {/* eslint-disable-next-line max-len */}
                <Icon className={classNames('text-xl kiosk:text-3xl text-default ml-3', collapsed ? 'fb-icon--arrow-down' : 'fb-icon--arrow-up')} />
              </div>
            </div>
          )}
        </div>
      </div>
      {hasCustomerPreAuthorizedAmount && (
        <PreAuthorizedAmountLine
          amount={customerPreAuthorizedAmount}
          className="w-full mt-5 p-4 kiosk:p-6 flex justify-between bg-card-bg rounded-card kiosk:text-2xl kioskSharebox:text-xl font-bold"
        />
      )}
    </>
  );
};

export default RepairOrderExpandableList;
